import { useEffect, useMemo, useState } from 'react';
import { EmailAuthProvider, linkWithCredential, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import ErrorMessage from '../forms/formComponents/ErrorMessage';
import { auth, db, functions } from '../../firebase-client';
import EmailInput from '../forms/formComponents/EmailInput';
import PasswordInput from '../forms/formComponents/PasswordInput';
import { PASSWORD_REQUIREMENTS_TEXT, passwordMeetsRequirements } from '../../utils/passwordValidation';
const finalizeChildAccountMigrationFn = httpsCallable(functions, 'finalizeChildAccountMigration');
function errorMessage(err, fallback) {
  if (!err) return fallback;
  const code = err.code || err?.customData?.code || '';
  if (code.includes('email-already-in-use')) return 'That email is already in use.';
  if (code.includes('invalid-email')) return 'Please enter a valid email address.';
  if (code.includes('weak-password')) return PASSWORD_REQUIREMENTS_TEXT;
  return err.message || fallback;
}
export default function ClaimStandaloneAccountCard({
  userId
}) {
  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [hasPasswordProvider, setHasPasswordProvider] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const currentUser = auth.currentUser;
      if (!currentUser || currentUser.uid !== userId) {
        if (!cancelled) setLoading(false);
        return;
      }
      const providerIds = (currentUser.providerData || []).map(provider => provider.providerId);
      const linkedPassword = providerIds.includes('password');
      const userSnap = await getDoc(doc(db, 'users', userId));
      const userData = userSnap.exists() ? userSnap.data() : {};
      const isChildAccount = userData?.accountType === 'child' || Boolean(userData?.createdByParentUid);
      if (!cancelled) {
        setEligible(isChildAccount);
        setHasPasswordProvider(linkedPassword);
        setEmailVerified(Boolean(currentUser.emailVerified));
        setEmail(currentUser.email || '');
        setLoading(false);
      }
    })().catch(err => {
      if (!cancelled) {
        setError('Failed to load account migration state.');
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);
  const readyToSubmit = useMemo(() => {
    return Boolean(email.trim() && password && confirmPassword);
  }, [email, password, confirmPassword]);
  const handleResendVerification = async () => {
    setError('');
    setInfo('');
    try {
      if (!auth.currentUser) throw new Error('You must be signed in.');
      await sendEmailVerification(auth.currentUser);
      setInfo('Verification email sent.');
    } catch (err) {
      setError(errorMessage(err, 'Failed to send verification email.'));
    }
  };
  const handleClaim = async e => {
    e.preventDefault();
    setError('');
    setInfo('');
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Email is required.');
      return;
    }
    if (!passwordMeetsRequirements(password)) {
      setError(PASSWORD_REQUIREMENTS_TEXT);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      if (!auth.currentUser || auth.currentUser.uid !== userId) {
        throw new Error('Session changed. Please refresh and try again.');
      }
      const credential = EmailAuthProvider.credential(normalizedEmail, password);
      try {
        await linkWithCredential(auth.currentUser, credential);
      } catch (linkErr) {
        const code = linkErr?.code || '';
        if (!code.includes('provider-already-linked')) throw linkErr;
      }
      try {
        await sendEmailVerification(auth.currentUser);
      } catch (verificationErr) {}
      const result = await finalizeChildAccountMigrationFn({
        email: normalizedEmail
      });
      const migratedEmail = result?.data?.email || normalizedEmail;
      setHasPasswordProvider(true);
      setEmail(migratedEmail);
      setPassword('');
      setConfirmPassword('');
      setInfo('Standalone login enabled. A verification email has been sent.');
    } catch (err) {
      setError(errorMessage(err, 'Failed to enable standalone login.'));
    } finally {
      setSubmitting(false);
    }
  };
  if (loading || !eligible) return null;
  return <div style={{
    marginTop: '1.5rem',
    padding: '1rem',
    border: '1px solid #e2e8f0',
    borderRadius: 8
  }}>
      <h2 style={{
      marginTop: 0,
      marginBottom: '0.5rem',
      fontSize: '1.1rem'
    }}>Standalone Login</h2>

      {hasPasswordProvider ? <>
          <p style={{
        marginTop: 0
      }}>
            This account can already log in directly with email/password
            {email ? ` (${email})` : ''}.
          </p>
          {!emailVerified && <button type="button" className="button button-secondary" onClick={handleResendVerification} disabled={submitting}>
              Resend Verification Email
            </button>}
        </> : <form onSubmit={handleClaim} className="flex-column" style={{
      gap: '0.75rem'
    }} autoComplete="on">
          <p style={{
        margin: 0
      }}>
            Add email login so this child account can be used independently.
          </p>
          <EmailInput
            inputId="standalone-login-email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <PasswordInput
            inputId="standalone-login-password"
            name="newPassword"
            value={password}
            onChange={e => setPassword(e.target.value)}
            confirmInputId="standalone-login-confirm-password"
            confirmName="confirmPassword"
            confirmValue={confirmPassword}
            onConfirmChange={e => setConfirmPassword(e.target.value)}
            showRequirements
          />
          <button className="button" type="submit" disabled={submitting || !readyToSubmit || !passwordMeetsRequirements(password) || password !== confirmPassword}>
            {submitting ? 'Enabling...' : 'Enable Standalone Login'}
          </button>
        </form>}

      {error && <ErrorMessage message={error} />}
      {info && <p style={{
      marginBottom: 0,
      color: '#0f766e'
    }}>{info}</p>}
    </div>;
}
